import { gsap } from 'gsap'

// 見た目の調整パラメータ
const CRACK_COUNT = 11        // 放射状のひびの本数
const CRACK_LENGTH_MIN = 14   // ひびの長さ（画面短辺に対する %）
const CRACK_LENGTH_MAX = 34
const HOLD_DURATION = 1.6     // ひびを表示し続ける秒数
const FADE_DURATION = 0.8     // フェードアウト秒数
const SHAKE_AMOUNT = 6        // 衝突時のキャンバスシェイク量（px）

/**
 * ScreenCrack — 「画面のガラスが割れた」フィルター演出。
 * 衝突点を中心にプロシージャルな SVG ひび割れとフラッシュを表示し、
 * 背後の WebGL キャンバスをシェイクさせる（ひび＝ガラスは動かさない）。
 */
export default class ScreenCrack {
  constructor() {
    this.container = document.createElement('div')
    this.container.className = 'screen-crack'
    this.container.setAttribute('aria-hidden', 'true')
    document.body.appendChild(this.container)

    this.canvasElement = document.querySelector('canvas.webgl')
  }

  /**
   * @param {number} xPercent 衝突点の画面 X（0-100）
   * @param {number} yPercent 衝突点の画面 Y（0-100）
   */
  show(xPercent, yPercent) {
    // 表示中に再衝突したら前のひびを片付けてから出し直す
    this.clear()

    const svg = this.createCrackSvg(xPercent, yPercent)
    const flash = document.createElement('div')
    flash.className = 'screen-crack-flash'
    flash.style.setProperty('--crack-x', `${xPercent}%`)
    flash.style.setProperty('--crack-y', `${yPercent}%`)

    this.container.appendChild(svg)
    this.container.appendChild(flash)

    this.timeline = gsap.timeline({ onComplete: () => this.clear() })

    // フラッシュ: 一瞬白く光って消える
    this.timeline.fromTo(flash, { opacity: 0 }, { opacity: 1, duration: 0.05, ease: 'none' }, 0)
    this.timeline.to(flash, { opacity: 0, duration: 0.25, ease: 'power2.out' }, 0.05)

    // ひび: 即表示 → 保持 → フェードアウト
    this.timeline.fromTo(svg, { opacity: 0 }, { opacity: 1, duration: 0.06, ease: 'none' }, 0)
    this.timeline.to(svg, { opacity: 0, duration: FADE_DURATION, ease: 'power1.in' }, HOLD_DURATION)

    // 衝突の衝撃で背後の世界（キャンバス）を揺らす
    if (this.canvasElement) {
      const shake = { amount: SHAKE_AMOUNT }
      this.timeline.to(shake, {
        amount: 0,
        duration: 0.35,
        ease: 'power2.out',
        onUpdate: () => {
          const a = shake.amount
          this.canvasElement.style.transform =
            `translate(${(Math.random() - 0.5) * 2 * a}px, ${(Math.random() - 0.5) * 2 * a}px)`
        },
        onComplete: () => { this.canvasElement.style.transform = '' },
      }, 0)
    }
  }

  // 衝突点から放射するギザギザのひび + 細い二次クラック + 中心を繋ぐ短い弧を生成
  createCrackSvg(xPercent, yPercent) {
    const svgNS = 'http://www.w3.org/2000/svg'
    const svg = document.createElementNS(svgNS, 'svg')
    svg.setAttribute('viewBox', '0 0 100 100')
    svg.setAttribute('preserveAspectRatio', 'none')
    svg.classList.add('screen-crack-svg')

    const cx = xPercent
    const cy = yPercent
    const crackTips = []

    for (let i = 0; i < CRACK_COUNT; i++) {
      // 全周にばらつきつつ均一すぎない角度
      const angle = (i / CRACK_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.7
      const length = CRACK_LENGTH_MIN + Math.random() * (CRACK_LENGTH_MAX - CRACK_LENGTH_MIN)
      const segments = 4 + Math.floor(Math.random() * 3)

      const points = [[cx, cy]]
      for (let s = 1; s <= segments; s++) {
        const dist = (s / segments) * length
        const jitter = (Math.random() - 0.5) * 4.5 * (s / segments) // 先端ほど折れ曲がる
        const jitterAngle = angle + Math.PI / 2
        points.push([
          cx + Math.cos(angle) * dist + Math.cos(jitterAngle) * jitter,
          cy + Math.sin(angle) * dist + Math.sin(jitterAngle) * jitter,
        ])
      }
      crackTips.push({ angle, points })

      const main = document.createElementNS(svgNS, 'polyline')
      main.setAttribute('points', points.map(p => p.join(',')).join(' '))
      main.setAttribute('class', 'screen-crack-line')
      svg.appendChild(main)

      // 途中から生える細い二次クラック
      if (Math.random() < 0.7) {
        const branchFrom = points[1 + Math.floor(Math.random() * (points.length - 2))]
        const branchAngle = angle + (Math.random() < 0.5 ? 1 : -1) * (0.5 + Math.random() * 0.7)
        const branchLength = length * (0.2 + Math.random() * 0.3)
        const branch = document.createElementNS(svgNS, 'polyline')
        branch.setAttribute('points', [
          branchFrom,
          [branchFrom[0] + Math.cos(branchAngle) * branchLength * 0.5,
           branchFrom[1] + Math.sin(branchAngle) * branchLength * 0.5],
          [branchFrom[0] + Math.cos(branchAngle) * branchLength + (Math.random() - 0.5) * 2,
           branchFrom[1] + Math.sin(branchAngle) * branchLength + (Math.random() - 0.5) * 2],
        ].map(p => p.join(',')).join(' '))
        branch.setAttribute('class', 'screen-crack-line screen-crack-line--thin')
        svg.appendChild(branch)
      }
    }

    // 中心付近: 隣のひび同士を繋ぐ短い弧（蜘蛛の巣感）
    for (let ring = 0; ring < 2; ring++) {
      const radius = 3 + ring * 4 + Math.random() * 2
      for (let i = 0; i < crackTips.length; i++) {
        if (Math.random() < 0.35) continue
        const a1 = crackTips[i].angle
        const a2 = crackTips[(i + 1) % crackTips.length].angle
        const arc = document.createElementNS(svgNS, 'polyline')
        const mid = (a1 + a2) / 2
        arc.setAttribute('points', [
          [cx + Math.cos(a1) * radius, cy + Math.sin(a1) * radius],
          [cx + Math.cos(mid) * radius * 1.12, cy + Math.sin(mid) * radius * 1.12],
          [cx + Math.cos(a2) * radius, cy + Math.sin(a2) * radius],
        ].map(p => p.join(',')).join(' '))
        arc.setAttribute('class', 'screen-crack-line screen-crack-line--thin')
        svg.appendChild(arc)
      }
    }

    return svg
  }

  clear() {
    this.timeline?.kill()
    this.timeline = null
    this.container.replaceChildren()
    if (this.canvasElement) this.canvasElement.style.transform = ''
  }
}
